<template>
  <div class="home">
    <!-- Home dashboard set + resolved: read-only render with its own clock -->
    <template v-if="dash">
      <div class="toolbar">
        <h2 class="dash-title" :title="dash.name">{{ dash.name }}</h2>
        <div class="toolbar-spacer" />
        <TimeRangePicker :model-value="clock.range.value" @update:model-value="clock.setRange" />
        <LgTooltip :text="liveTitle" placement="bottom">
          <LgButton
            class="live-toggle"
            :class="clock.live.value ? 'live' : 'off'"
            size="sm"
            variant="ghost"
            :aria-pressed="clock.live.value"
            @click="clock.live.value = !clock.live.value"
          >
            <template #icon>
              <Play v-if="clock.live.value" :size="14" aria-hidden="true" />
              <Pause v-else :size="14" aria-hidden="true" />
            </template>
            {{ clock.live.value ? 'Live' : 'Paused' }}
          </LgButton>
        </LgTooltip>
      </div>

      <div class="body">
        <DashboardGrid
          :tiles="tiles"
          :from="clock.from.value"
          :to="clock.to.value"
          :live="clock.live.value"
          @unauthorized="$emit('unauthorized')"
        />
      </div>
    </template>

    <div v-else-if="loading" class="state">
      <p>Loading your home dashboard…</p>
    </div>

    <!-- No home dashboard chosen (or it was deleted) -->
    <div v-else class="state">
      <GoblinIcon variant="mark" :size="64" />
      <p class="state-title">No home dashboard yet</p>
      <p class="state-sub">
        Pick a dashboard as your home from the Dashboards tab — star one and it will greet you here
        every time you sign in.
      </p>
      <LgButton variant="primary" @click="$emit('go-dashboards')">
        <template #icon><LayoutDashboard :size="14" aria-hidden="true" /></template>
        Go to Dashboards
      </LgButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { LayoutDashboard, Play, Pause } from 'lucide-vue-next';
import { api, ApiError, type ResolvedTile } from '../api.js';
import { useDashboardClock } from '../composables/useDashboardClock.js';
import { normalizeTiles } from '../lib/dashboard-layout.js';
import { SCRAPE_INTERVAL_MS } from '../lib/metric-exploration.js';
import DashboardGrid from '../components/dashboards/DashboardGrid.vue';
import TimeRangePicker from '../components/TimeRangePicker.vue';
import GoblinIcon from '../components/GoblinIcon.vue';
import LgButton from '../ui/LgButton.vue';
import LgTooltip from '../ui/LgTooltip.vue';

const emit = defineEmits<{ unauthorized: []; 'go-dashboards': [] }>();

const loading = ref(true);
const dash = ref<{ id: number; name: string } | null>(null);
const tiles = ref<ResolvedTile[]>([]);

const clock = useDashboardClock();
const liveTitle = computed(() =>
  clock.live.value
    ? `Every tile refreshes every ${SCRAPE_INTERVAL_MS / 1000}s. Click to pause.`
    : 'The window is frozen. Click to resume.',
);

onMounted(async () => {
  try {
    const homeId = await api.getHomeDashboardId();
    if (homeId === null) return;
    const resolved = await api.dashboards.resolved(homeId);
    dash.value = { id: resolved.id, name: resolved.name };
    tiles.value = normalizeTiles(resolved.layout.tiles);
    clock.reset(resolved.timeDefault);
  } catch (e) {
    // A dangling/deleted home dashboard (404) degrades to the friendly empty
    // state rather than an error — the backend prunes the setting on read.
    if (e instanceof ApiError && e.unauthorized) emit('unauthorized');
    dash.value = null;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.home {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
  padding: var(--lg-space-2) var(--lg-space-3);
  border-bottom: 1px solid var(--lg-border);
  flex-shrink: 0;
}
.toolbar-spacer {
  flex: 1;
  min-width: 0;
}
.dash-title {
  margin: 0;
  font-size: var(--lg-fs-md);
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-toggle.live {
  color: var(--lg-ok);
}
.live-toggle.off {
  color: var(--lg-text-faint);
}
.body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--lg-space-3);
}
.state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-2);
  padding: var(--lg-space-8) var(--lg-space-4);
  color: var(--lg-text-faint);
  text-align: center;
}
.state-title {
  margin: 0;
  font-size: var(--lg-fs-lg);
  color: var(--lg-text-dim);
}
.state-sub {
  margin: 0;
  font-size: var(--lg-fs-sm);
  max-width: 46ch;
}
@media (max-width: 768px) {
  .body {
    padding: var(--lg-space-2);
  }
}
</style>
